import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import '../styles/EcosystemAnalysisEnhanced.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';

// Metal ecosystem stocks organized by category
const METAL_ECOSYSTEM = {
  'Industrial & Materials': {
    'CAT': { name: 'Caterpillar Inc', market: 'US' },
    'ITW': { name: 'Illinois Tool Works', market: 'US' },
    'MMM': { name: '3M Company', market: 'US' },
    'HON': { name: 'Honeywell International', market: 'US' },
    'RTX': { name: 'Raytheon Technologies', market: 'US' }
  },
  'Metals & Refining': {
    'PPL': { name: 'PPL Corporation', market: 'US' },
    'CVX': { name: 'Chevron', market: 'US' },
    'COP': { name: 'ConocoPhillips', market: 'US' },
    'XOM': { name: 'ExxonMobil', market: 'US' },
    'OXY': { name: 'Occidental Petroleum', market: 'US' }
  },
  'Energy & Infrastructure': {
    'EOG': { name: 'EOG Resources', market: 'US' },
    'WEC': { name: 'WEC Energy Group', market: 'US' },
    'DUK': { name: 'Duke Energy', market: 'US' },
    'SO': { name: 'Southern Company', market: 'US' },
    'NEE': { name: 'NextEra Energy', market: 'US' }
  },
  'Data Center & Logistics': {
    'DLR': { name: 'Digital Realty', market: 'US' },
    'EQIX': { name: 'Equinix', market: 'US' },
    'CCI': { name: 'Crown Castle', market: 'US' },
    'AMT': { name: 'American Tower', market: 'US' },
    'PLD': { name: 'Prologis', market: 'US' }
  }
};

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#82d982', '#ffb347'];

const DEFAULT_TIMEOUT = 3000;

export default function MetalEcosystemAnalysisEnhanced() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('change');
  const [refreshTime, setRefreshTime] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid'); // grid, chart, list
  const [watchlist, setWatchlist] = useState(() => {
    const saved = localStorage.getItem('metalWatchlist');
    return saved ? JSON.parse(saved) : [];
  });

  const fetchStockData = async () => {
    setLoading(true);
    setError(null);
    try {
      const allStocks = [];
      const stocksToFetch = [];

      // Collect all unique stocks
      for (const category in METAL_ECOSYSTEM) {
        for (const symbol in METAL_ECOSYSTEM[category]) {
          stocksToFetch.push({ symbol, category });
        }
      }

      // Fetch data in parallel
      const promises = stocksToFetch.map(async ({ symbol, category }) => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

          const [priceRes, newsRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/stock/${symbol}/details`, { signal: controller.signal })
              .then(r => r.ok ? r.json() : { error: true }),
            fetch(`${API_BASE_URL}/api/stock/${symbol}/news`, { signal: controller.signal })
              .then(r => r.ok ? r.json() : { news: [] })
          ]);

          clearTimeout(timeoutId);

          const priceData = priceRes.error ? null : priceRes;
          const newsData = newsRes || { news: [] };

          allStocks.push({
            symbol,
            category,
            name: METAL_ECOSYSTEM[category]?.[symbol]?.name || symbol,
            market: METAL_ECOSYSTEM[category]?.[symbol]?.market || 'US',
            current_price: priceData?.price || 0,
            day_change_percent: priceData?.change_percent || 0,
            day_change: priceData?.change || 0,
            market_cap: priceData?.market_cap || 0,
            pe_ratio: priceData?.pe_ratio || 0,
            high_52w: priceData?.high52 || 0,
            low_52w: priceData?.low52 || 0,
            volume: priceData?.volume || 0,
            avg_volume: priceData?.avg_volume || 0,
            news: newsData.news || []
          });
        } catch (err) {
          console.error(`Error fetching data for ${symbol}:`, err);
        }
      });

      await Promise.all(promises);

      setStocks(allStocks);
      setError(null);
    } catch (err) {
      setError('Failed to fetch ecosystem data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockData();
    const interval = setInterval(() => {
      fetchStockData();
      setRefreshTime(new Date());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const getDisplayedStocks = () => {
    let displayed = stocks;

    if (selectedCategory !== 'all') {
      displayed = stocks.filter(s => s.category === selectedCategory);
    }

    displayed.sort((a, b) => {
      if (sortBy === 'change') {
        return (b.day_change_percent || 0) - (a.day_change_percent || 0);
      } else if (sortBy === 'price') {
        return (b.current_price || 0) - (a.current_price || 0);
      } else if (sortBy === 'symbol') {
        return a.symbol.localeCompare(b.symbol);
      }
      return 0;
    });

    return displayed;
  };

  const displayedStocks = getDisplayedStocks();
  const categories = Object.keys(METAL_ECOSYSTEM);

  const getCategoryStats = () => {
    if (selectedCategory === 'all') {
      return {
        count: displayedStocks.length,
        avgChange: displayedStocks.length > 0 
          ? (displayedStocks.reduce((sum, s) => sum + (s.day_change_percent || 0), 0) / displayedStocks.length).toFixed(2)
          : 0,
        topGainer: displayedStocks.length > 0 ? displayedStocks.reduce((max, s) => (s.day_change_percent || 0) > (max.day_change_percent || 0) ? s : max) : null,
        topLoser: displayedStocks.length > 0 ? displayedStocks.reduce((min, s) => (s.day_change_percent || 0) < (min.day_change_percent || 0) ? s : min) : null
      };
    }
    return {
      count: displayedStocks.length,
      avgChange: displayedStocks.length > 0 
        ? (displayedStocks.reduce((sum, s) => sum + (s.day_change_percent || 0), 0) / displayedStocks.length).toFixed(2)
        : 0,
      topGainer: displayedStocks.length > 0 ? displayedStocks.reduce((max, s) => (s.day_change_percent || 0) > (max.day_change_percent || 0) ? s : max) : null,
      topLoser: displayedStocks.length > 0 ? displayedStocks.reduce((min, s) => (s.day_change_percent || 0) < (min.day_change_percent || 0) ? s : min) : null
    };
  };

  const stats = getCategoryStats();

  const chartData = displayedStocks.map(s => ({
    symbol: s.symbol,
    change: parseFloat((s.day_change_percent || 0).toFixed(2)),
    price: parseFloat((s.current_price || 0).toFixed(2)),
    peRatio: parseFloat((s.pe_ratio || 0).toFixed(2))
  })).slice(0, 10);

  const categoryDistribution = Object.keys(METAL_ECOSYSTEM).map(cat => ({
    name: cat,
    value: Object.keys(METAL_ECOSYSTEM[cat]).length
  }));

  const toggleWatchlist = (symbol) => {
    setWatchlist(prev => {
      const updated = prev.includes(symbol) 
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol];
      localStorage.setItem('metalWatchlist', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div className="ecosystem-container-enhanced">
      <div className="ecosystem-header-enhanced">
        <h1>⚙️ Industrial & Materials Ecosystem - Advanced</h1>
        <p className="subtitle">Manufacturing, Metals, Energy & Infrastructure Market Analysis with Charts & Metrics</p>
        
        <div className="header-stats-enhanced">
          <div className="stat-card-enhanced">
            <span className="label">Stocks Monitored</span>
            <span className="value">{displayedStocks.length}</span>
          </div>
          <div className="stat-card-enhanced">
            <span className="label">Avg Daily Change</span>
            <span className={`value ${stats.avgChange >= 0 ? 'positive' : 'negative'}`}>
              {stats.avgChange >= 0 ? '+' : ''}{stats.avgChange}%
            </span>
          </div>
          {stats.topGainer && (
            <div className="stat-card-enhanced">
              <span className="label">Top Gainer</span>
              <span className="value positive">{stats.topGainer.symbol} +{(stats.topGainer.day_change_percent || 0).toFixed(2)}%</span>
            </div>
          )}
          {stats.topLoser && (
            <div className="stat-card-enhanced">
              <span className="label">Top Loser</span>
              <span className="value negative">{stats.topLoser.symbol} {(stats.topLoser.day_change_percent || 0).toFixed(2)}%</span>
            </div>
          )}
          <div className="stat-card-enhanced">
            <span className="label">Last Updated</span>
            <span className="value">{refreshTime.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      <div className="controls-enhanced">
        <div className="control-group-enhanced">
          <label>Category:</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="control-group-enhanced">
          <label>Sort By:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="change">Daily Change %</option>
            <option value="price">Price</option>
            <option value="symbol">Symbol</option>
          </select>
        </div>

        <div className="control-group-enhanced">
          <label>View:</label>
          <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
            <option value="grid">Grid</option>
            <option value="chart">Charts</option>
            <option value="list">List</option>
          </select>
        </div>

        <button className="refresh-btn-enhanced" onClick={fetchStockData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>

        <button className="watchlist-btn-enhanced" title={`${watchlist.length} in watchlist`}>
          ⭐ Watchlist ({watchlist.length})
        </button>
      </div>

      {error && (
        <div className="error-box-enhanced">
          <p>{error}</p>
          <button onClick={fetchStockData}>Retry</button>
        </div>
      )}

      {loading && (
        <div className="loading-enhanced">
          <div className="spinner-enhanced"></div>
          <p>Loading ecosystem data...</p>
        </div>
      )}

      {!loading && viewMode === 'grid' && (
        <div className="stocks-grid-enhanced">
          {displayedStocks.map(stock => (
            <div key={stock.symbol} className="stock-card-enhanced">
              <div className="stock-card-header-enhanced">
                <div>
                  <h3 className="stock-symbol-enhanced">{stock.symbol}</h3>
                  <p className="stock-category-enhanced">{stock.category}</p>
                </div>
                <button 
                  className={`watchlist-toggle ${watchlist.includes(stock.symbol) ? 'active' : ''}`}
                  onClick={() => toggleWatchlist(stock.symbol)}
                  title="Add to watchlist"
                >
                  {watchlist.includes(stock.symbol) ? '⭐' : '☆'}
                </button>
              </div>

              <div className="stock-body-enhanced">
                <div className="price-section-enhanced">
                  <span className="current-price-enhanced">${(stock.current_price || 0).toFixed(2)}</span>
                  <span className={`daily-change-enhanced ${stock.day_change_percent >= 0 ? 'positive' : 'negative'}`}>
                    {stock.day_change_percent >= 0 ? '+' : ''}{(stock.day_change_percent || 0).toFixed(2)}%
                  </span>
                </div>

                <div className="metrics-grid-enhanced">
                  <div className="metric-item-enhanced">
                    <span className="metric-label-enhanced">Market Cap</span>
                    <span className="metric-value-enhanced">${stock.market_cap > 0 ? (stock.market_cap / 1e9).toFixed(1) : 'N/A'}B</span>
                  </div>
                  <div className="metric-item-enhanced">
                    <span className="metric-label-enhanced">P/E Ratio</span>
                    <span className="metric-value-enhanced">{(stock.pe_ratio || 0).toFixed(2)}</span>
                  </div>
                  <div className="metric-item-enhanced">
                    <span className="metric-label-enhanced">52W High</span>
                    <span className="metric-value-enhanced">${(stock.high_52w || 0).toFixed(2)}</span>
                  </div>
                  <div className="metric-item-enhanced">
                    <span className="metric-label-enhanced">52W Low</span>
                    <span className="metric-value-enhanced">${(stock.low_52w || 0).toFixed(2)}</span>
                  </div>
                  <div className="metric-item-enhanced">
                    <span className="metric-label-enhanced">Volume</span>
                    <span className="metric-value-enhanced">{stock.volume > 0 ? (stock.volume / 1e6).toFixed(1) : 'N/A'}M</span>
                  </div>
                  <div className="metric-item-enhanced">
                    <span className="metric-label-enhanced">Avg Volume</span>
                    <span className="metric-value-enhanced">{stock.avg_volume > 0 ? (stock.avg_volume / 1e6).toFixed(1) : 'N/A'}M</span>
                  </div>
                </div>

                {stock.news && stock.news.length > 0 && (
                  <div className="news-section-enhanced">
                    <div className="news-title-enhanced">Latest News</div>
                    <div className="news-list-enhanced">
                      {stock.news.slice(0, 3).map((article, idx) => (
                        <div key={idx} className="news-item-enhanced">
                          <p className="news-headline-enhanced">{article.title || 'No headline'}</p>
                          {article.source && <span className="news-source-enhanced">{article.source}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && viewMode === 'chart' && (
        <div className="charts-container-enhanced">
          <div className="chart-card-enhanced">
            <h3>Daily Change % (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="symbol" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="change" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card-enhanced">
            <h3>Stock Prices (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="symbol" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="price" stroke="#82ca9d" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card-enhanced">
            <h3>Category Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={categoryDistribution} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {categoryDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card-enhanced">
            <h3>P/E Ratio Comparison (Top 10)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="symbol" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="peRatio" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!loading && viewMode === 'list' && (
        <div className="list-view-enhanced">
          <table className="stocks-table-enhanced">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Category</th>
                <th>Price</th>
                <th>Change %</th>
                <th>Market Cap</th>
                <th>P/E</th>
                <th>52W High</th>
                <th>52W Low</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedStocks.map(stock => (
                <tr key={stock.symbol}>
                  <td className="symbol-td-enhanced">{stock.symbol}</td>
                  <td>{stock.category}</td>
                  <td>${(stock.current_price || 0).toFixed(2)}</td>
                  <td className={stock.day_change_percent >= 0 ? 'positive' : 'negative'}>
                    {stock.day_change_percent >= 0 ? '+' : ''}{(stock.day_change_percent || 0).toFixed(2)}%
                  </td>
                  <td>${stock.market_cap > 0 ? (stock.market_cap / 1e9).toFixed(1) : 'N/A'}B</td>
                  <td>{(stock.pe_ratio || 0).toFixed(2)}</td>
                  <td>${(stock.high_52w || 0).toFixed(2)}</td>
                  <td>${(stock.low_52w || 0).toFixed(2)}</td>
                  <td>
                    <button 
                      className={`watchlist-btn-small ${watchlist.includes(stock.symbol) ? 'active' : ''}`}
                      onClick={() => toggleWatchlist(stock.symbol)}
                    >
                      {watchlist.includes(stock.symbol) ? '⭐' : '☆'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
